import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';

const AddEditStay = () => {
  const { stayId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(stayId);

  const [formData, setFormData] = useState({
    title: '',
    title_en: '',
    date: '',
    location: '',
    type: 'Grand Prix',
    description: '',
    description_en: '',
    duration: '',
    image: '',
    flight_info: '',
    price_member_with_flight: 0,
    price_member_no_flight: 0,
    price_non_member_with_flight: 0,
    price_non_member_no_flight: 0,
    total_seats: 0,
    seats_sold: 0,
    status: 'Disponible',
    active: true,
    detail_link: ''
  });

  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);

  const stayTypes = ['Grand Prix', 'Croisière', 'Séminaire', 'Escapade', 'Autre'];
  const statusOptions = ['Disponible', 'Complet', 'Annulé', 'Terminé'];

  useEffect(() => {
    if (isEdit) {
      fetchStay();
    }
  }, [stayId]);

  const fetchStay = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stays/${stayId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const stay = await response.json();
        setFormData({
          title: stay.title || '',
          title_en: stay.title_en || '',
          date: stay.date || '',
          location: stay.location || '',
          type: stay.type || 'Grand Prix',
          description: stay.description || '',
          description_en: stay.description_en || '',
          duration: stay.duration || '',
          image: stay.image || '',
          flight_info: stay.flight_info || '',
          price_member_with_flight: stay.price_member_with_flight || 0,
          price_member_no_flight: stay.price_member_no_flight || 0,
          price_non_member_with_flight: stay.price_non_member_with_flight || 0,
          price_non_member_no_flight: stay.price_non_member_no_flight || 0,
          total_seats: stay.total_seats || 0,
          seats_sold: stay.seats_sold || 0,
          status: stay.status || 'Disponible',
          active: stay.active !== false,
          detail_link: stay.detail_link || ''
        });
        if (stay.image) {
          if (stay.image.startsWith('http')) {
            setImagePreview(stay.image);
          } else {
            const previewUrl = stay.image.replace('/uploads/', '/api/uploads/');
            setImagePreview(`${process.env.REACT_APP_BACKEND_URL}${previewUrl}`);
          }
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("L'image est trop volumineuse (max 5MB)");
      return;
    }

    setUploadingImage(true);

    try {
      const token = localStorage.getItem('adminToken');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/stays/upload-image`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataUpload
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image: data.url }));
        const previewUrl = data.url.replace('/uploads/', '/api/uploads/');
        setImagePreview(`${process.env.REACT_APP_BACKEND_URL}${previewUrl}`);
        alert('Image uploadée avec succès !');
      } else {
        alert("Erreur lors de l'upload de l'image");
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert("Erreur lors de l'upload de l'image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const url = isEdit
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/stays/${stayId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/stays`;

      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert(isEdit ? 'Séjour mis à jour avec succès' : 'Séjour créé avec succès');
        navigate('/admin/stays');
      } else {
        const error = await response.json();
        alert('Erreur: ' + (error.detail || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <AdminLayout>
        <div style={{ padding: '32px' }}>
          <p>Chargement...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: '32px' }}>
        <button
          onClick={() => navigate('/admin/stays')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '24px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-secondary)'
          }}
        >
          <ArrowLeft size={20} />
          <span className="body-regular">Retour</span>
        </button>

        <h1 className="hero-medium" style={{ marginBottom: '32px' }}>
          {isEdit ? 'Modifier le séjour' : 'Ajouter un séjour'}
        </h1>

        <form onSubmit={handleSubmit} style={{
          maxWidth: '900px',
          background: 'var(--bg-primary)',
          border: '1px solid var(--border-light)',
          padding: '32px'
        }}>
          {/* Informations de base */}
          <h3 className="heading-3" style={{ marginBottom: '24px', color: '#2c5f7d' }}>
            🏨 Informations de base
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Titre (FR) *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="ex: Grand Prix de Monaco"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Titre (EN)
              </label>
              <input
                type="text"
                name="title_en"
                value={formData.title_en}
                onChange={handleChange}
                placeholder="ex: Monaco Grand Prix"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Type *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  background: 'var(--bg-primary)'
                }}
              >
                {stayTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Date *
              </label>
              <input
                type="text"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                placeholder="ex: 5-7 Juin 2026"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Lieu *
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                placeholder="ex: Monte-Carlo, Monaco"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Durée *
              </label>
              <input
                type="text"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                required
                placeholder="ex: 3 jours / 2 nuits"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Champ libre pour l'avion et le descriptif du vol */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                ✈️ Avion & Descriptif du vol
              </label>
              <textarea
                name="flight_info"
                value={formData.flight_info}
                onChange={handleChange}
                rows={3}
                placeholder="ex: Vol privé Paris-Nice en Citation XLS + Hélicoptère Nice-Monaco. Départ 10h00, arrivée 12h30."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
              <p className="body-small" style={{ marginTop: '4px', color: 'var(--text-secondary)' }}>
                Indiquez ici les détails de l'avion et du vol associé au séjour
              </p>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Description (FR) *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={4}
                placeholder="Description détaillée du séjour..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Description (EN)
              </label>
              <textarea
                name="description_en"
                value={formData.description_en}
                onChange={handleChange}
                rows={4}
                placeholder="Detailed description of the stay..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>

          {/* Image */}
          <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid var(--border-light)' }}>
            <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              🖼️ Image
            </label>
            <p className="body-small" style={{ marginBottom: '12px', color: 'var(--text-secondary)' }}>
              📐 Dimensions recommandées : 800 x 400 px • Format : JPG ou PNG • Poids : &lt; 500 KB
            </p>

            {imagePreview && (
              <div style={{ marginBottom: '12px' }}>
                <img
                  src={imagePreview}
                  alt="Aperçu"
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    height: 'auto',
                    border: '1px solid var(--border-light)'
                  }}
                />
              </div>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploadingImage}
              style={{
                padding: '12px',
                border: '1px solid var(--border-light)',
                fontSize: '14px',
                fontFamily: 'inherit',
                cursor: uploadingImage ? 'not-allowed' : 'pointer',
                width: '100%'
              }}
            />
            {uploadingImage && (
              <p className="body-small" style={{ marginTop: '8px', color: '#2c5f7d' }}>
                Upload en cours...
              </p>
            )}

            <div style={{ marginTop: '12px' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Ou URL externe de l'image
              </label>
              <input
                type="text"
                name="image"
                value={formData.image}
                onChange={(e) => {
                  handleChange(e);
                  setImagePreview(e.target.value);
                }}
                placeholder="https://..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>
          </div>

          {/* Tarification */}
          <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '2px solid #c41e3a' }}>
            <h3 className="heading-3" style={{ marginBottom: '24px', color: '#c41e3a' }}>
              💰 Tarification
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Prix Membre AVEC vol (€) *
                </label>
                <input
                  type="number"
                  name="price_member_with_flight"
                  value={formData.price_member_with_flight}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Prix Membre SANS vol (€) *
                </label>
                <input
                  type="number"
                  name="price_member_no_flight"
                  value={formData.price_member_no_flight}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Prix Non-membre AVEC vol (€) *
                </label>
                <input
                  type="number"
                  name="price_non_member_with_flight"
                  value={formData.price_non_member_with_flight}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Prix Non-membre SANS vol (€) *
                </label>
                <input
                  type="number"
                  name="price_non_member_no_flight"
                  value={formData.price_non_member_no_flight}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Gestion des places */}
          <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid var(--border-light)' }}>
            <h3 className="heading-3" style={{ marginBottom: '24px', color: '#2c5f7d' }}>
              👥 Gestion des places
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Places totales *
                </label>
                <input
                  type="number"
                  name="total_seats"
                  value={formData.total_seats}
                  onChange={handleChange}
                  required
                  min="0"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Places vendues *
                </label>
                <input
                  type="number"
                  name="seats_sold"
                  value={formData.seats_sold}
                  onChange={handleChange}
                  required
                  min="0"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Statut *
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    background: 'var(--bg-primary)'
                  }}
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: '16px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  style={{ cursor: 'pointer' }}
                />
                <span className="body-regular">Séjour actif (visible sur le site)</span>
              </label>
            </div>
          </div>

          {/* Lien de détail */}
          <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid var(--border-light)' }}>
            <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              🔗 Lien vers la page de détail (optionnel)
            </label>
            <input
              type="text"
              name="detail_link"
              value={formData.detail_link}
              onChange={handleChange}
              placeholder="ex: /member/grand-prix-monaco"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border-light)',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Boutons */}
          <div style={{
            marginTop: '32px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={() => navigate('/admin/stays')}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                border: '1px solid var(--border-light)',
                cursor: 'pointer',
                color: 'var(--text-secondary)'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: loading ? 0.6 : 1
              }}
            >
              <Save size={20} />
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AddEditStay;
